import { initializeApp, deleteApp, type FirebaseApp } from 'firebase/app'
import { getAuth, signOut as firebaseSignOut, type Auth } from 'firebase/auth'
import { getDatabase, type Database } from 'firebase/database'
import { useRuntimeConfig } from '#app'
import { useBugsnag } from '#imports'

interface FirebaseConfig {
  apiKey: string
  authDomain: string
  databaseURL: string
  projectId: string
}

export interface FirebaseService {
  app: FirebaseApp | null
  database: Database | null
  auth: Auth | null
  isInitialized: boolean
}

const REQUIRED_CONFIG_KEYS: (keyof FirebaseConfig)[] = [
  'apiKey',
  'authDomain',
  'databaseURL',
  'projectId'
] as const

let app: FirebaseApp | null = null
let database: Database | null = null
let auth: Auth | null = null

function validateFirebaseConfig (config: Partial<FirebaseConfig>): config is FirebaseConfig {
  for (const key of REQUIRED_CONFIG_KEYS) {
    const value = config[key]
    if (!value || typeof value !== 'string' || value.trim() === '') {
      return false
    }
  }

  return true
}

export function useFirebase (
  mockApp?: FirebaseApp,
  mockDatabase?: Database
): FirebaseService {
  if (mockApp && mockDatabase) {
    return { app: mockApp, database: mockDatabase, auth: null, isInitialized: true }
  }

  if (process.server) {
    return { app: null, database: null, auth: null, isInitialized: false }
  }

  if (!app) {
    const config = useRuntimeConfig().public.firebase as Partial<FirebaseConfig>

    if (!validateFirebaseConfig(config)) {
      const bugsnag = useBugsnag()
      bugsnag?.notify(new Error('Firebase configuration is invalid or incomplete'), (event) => {
        event.addMetadata('firebase', { providedKeys: Object.keys(config), requiredKeys: REQUIRED_CONFIG_KEYS })
      })
      return { app: null, database: null, auth: null, isInitialized: false }
    }

    try {
      app = initializeApp(config)
      database = getDatabase(app)
      auth = getAuth(app)
    } catch (error) {
      const bugsnag = useBugsnag()
      bugsnag?.notify(error as Error, (event) => {
        event.addMetadata('firebase', { message: 'Failed to initialize Firebase' })
      })
      return { app: null, database: null, auth: null, isInitialized: false }
    }
  }

  return {
    app,
    database,
    auth,
    isInitialized: app !== null && database !== null
  }
}

export async function disconnectFirebase (): Promise<void> {
  if (app) {
    try {
      if (auth) {
        await firebaseSignOut(auth)
      }
      await deleteApp(app)
    } catch (error) {
      const bugsnag = useBugsnag()
      bugsnag?.notify(error as Error, (event) => {
        event.addMetadata('firebase', { message: 'Failed to disconnect Firebase' })
      })
    } finally {
      app = null
      database = null
      auth = null
    }
  }
}
